import { Injectable } from '@angular/core';
import { DiaryService } from './diary.service';
import { UserDataFormatter } from './user-data.formatter';
import { UserInfoService } from './user.info.service';
import { PeriodEvent } from '../models/event';
import { Cycle, CycleStatus, Pregnancy } from '../models/period';
import { ChatConfigurationService } from './chat.configuration.service';
import { EventService } from './event.service';
import { UserProfile } from '../models/user';
import { PeriodService } from './period.service';
import { AggregatesService } from './aggregates.service';
import { WellnessAggregate } from '../models/wellness.agg';

@Injectable({
  providedIn: 'root'
})
export class ChatDataService {
  constructor(
    private diaryService: DiaryService,
    private eventService: EventService,
    private userInfoService: UserInfoService,
    private chatConfigurationService: ChatConfigurationService,
    private periodService: PeriodService,
    private aggregatesService: AggregatesService
  ) { }


  async getUserDataMessage(): Promise<{message: string, source: string} | null> {
    return await this.chatConfigurationService.readUserDataMessage();
  }

  async generateUserDataMessage() {
    let userProfile = await this.userInfoService.getUserProfile();
    let userInfoHealth = await this.userInfoService.getReproductiveHealth();
    let periods = await this.diaryService.getUserPeriods();
    let pregnancies = await this.diaryService.getUserPregnancies();
    let cycles = this.diaryService.buildCycles(periods, userInfoHealth?.cycleLengthPredicted ?? 28);
    let diary = await this.diaryService.getUserDiaryOnce();
    let cyclePhase = await this.periodService.calculatePhase();
    let contraception = this.userInfoService.getUserInfoContraception(userInfoHealth);
    let isPregnancyOrPostpartum = cyclePhase?.state == "Pregnancy" || cyclePhase?.state == "Postpartum";
    let aggregates = await this.aggregatesService.fetchLast30WellnessDays();
    let hasIVYPlus = await this.userInfoService.hasIVPDeviceOnce();

    this._generateUserDataMessage(
      cycles, 
      pregnancies, 
      diary, 
      userInfoHealth?.shouldTrackFertileWindow ?? true, 
      contraception, 
      userProfile, 
      isPregnancyOrPostpartum, 
      userInfoHealth?.cycleLengthPredicted ?? 28, 
      cyclePhase.state ?? 'No data', 
      aggregates, 
      hasIVYPlus,
      userInfoHealth?.usageReason
    );
  }

  _generateUserDataMessage(
    cycles: Cycle[],
    pregnancies: Pregnancy[],
    events: PeriodEvent[],
    shouldTrackFertileWindow: boolean,
    contraception: any,
    userProfile: UserProfile,
    isPregnancyOrPostpartum: boolean,
    cycleLength: number,
    cyclePhase: string,
    wellnessAggregates: WellnessAggregate[],
    hasIVPDevice: boolean,
    usageReason: string | null,
  ) {
    let message = "User's personal health data: ";

    const currentDate = beginningOfLocalDay();

    const pregnanciesDataMessage = this.generatePregnanciesMessage(pregnancies);
    const preparedCycles = this.prepareCycles(cycles);
    const cycleHeader =
      preparedCycles.length === 0 ? "" : UserDataFormatter.generateHeader("cycle");
    const cyclesDataMessage = this.generateCycleMessage(preparedCycles, "past", isPregnancyOrPostpartum);
    const currentCycleData = this.generateCycleMessage(preparedCycles, "current", isPregnancyOrPostpartum);
    const predictedCycleData = this.generateCycleMessage(preparedCycles, "predicted", isPregnancyOrPostpartum);
    const wellnesAggregateMessage = this.generateWellnessAggregateMessage(wellnessAggregates);
    const additionalDataMessage = UserDataFormatter.formatAdditionalReproductiveHealthData(
      {
        cycleLength,
        shouldTrackFertileWindow,
        currentCycleData: currentCycleData,
        predictedCycleData: predictedCycleData,
        cycles,
        inPregnancyOrPostPartum: isPregnancyOrPostpartum,
        cyclePhase,
        usageReason
      }
    );

    const contraceptionDataMessage = UserDataFormatter.formatUserContraception(
      contraception
    );

    const eventsDataMessage = this.generateEventsMessage(events);
    const todayDate = UserDataFormatter.formatCurrentDay(currentDate);
    const hasDeviceMessage = this.generateDeviceMessage(hasIVPDevice);

    message += pregnanciesDataMessage;
    message += cycleHeader;
    message += cyclesDataMessage;
    message += additionalDataMessage;
    message += contraceptionDataMessage;
    message += eventsDataMessage;
    message += wellnesAggregateMessage;
    message += hasDeviceMessage;

    if (userProfile) {
      const userProfileMessage = UserDataFormatter.formatUserDataFromProfile(
        userProfile,
      );
      message += userProfileMessage;
    }

     message += todayDate;

    this.chatConfigurationService.saveUserDataMessage(message);
    return message;
  }

  private generateCycleMessage(cycles: Cycle[], type: CycleStatus, isPregnancyOrPostpartum: boolean): string {
    const cyclesToSend = cycles.filter(c => c.type === type);
    if (cyclesToSend.length === 0) return '';
    return cyclesToSend
      .map(cycle => UserDataFormatter.formatCycle(cycle, isPregnancyOrPostpartum))
      .join(' ');
  }

  private generateDeviceMessage(hasIVYPlus: boolean) {
    if (hasIVYPlus) {
      return ` User has IVY+ Device; `;
    } else {
      return ` User DOES NOT have IVY+ Device; `;
    }
  }

  private generateWellnessAggregateMessage(agg: WellnessAggregate[]): string {
    if (!agg || agg.length === 0) {
      return ``;
    }

    const rows = [...agg].sort((a, b) => a.day.localeCompare(b.day));

    const sleepLine = rows
      .map((e) => {
        const deep = e.sleep?.deepMinutes ?? 0;
        const light = e.sleep?.lightMinutes ?? 0;
        const nap = e.sleep?.napMinutes ?? 0;
        return `${e.day}: { ${light} light minutes, ${deep} deep minutes, ${nap} nap minutes }`;
      })
      .join("  ");

    const activityLine = rows
      .map((e) => {
        const active = e.activity?.activeMinutes ?? 0;
        return `${e.day}: ${active} mins`;
      })
      .join(", ");

    const stepsLine = rows
      .map((e) => {
        const steps = e.steps?.totalSteps ?? 0;
        return `${e.day}: ${steps} steps`;
      })
      .join(", ");

    return `Sleep: ${sleepLine}\nActivity: ${activityLine}\nSteps: ${stepsLine};`;
  }

  private prepareCycles(cycles: Cycle[]): Cycle[] {
    const currentDate = beginningOfLocalDay();

    const firstPredictedFuture = cycles.find(
      c => c.type === 'predicted' && toStartOfLocalDay(c.startedOn) >= currentDate
    );

    const nonPredicted = cycles.filter(c => c.type !== 'predicted');
    const lastN = 14

    const recent = nonPredicted.slice(-lastN);

    return firstPredictedFuture ? [...recent, firstPredictedFuture] : recent;
  }

  private generatePregnanciesMessage(pregnancies: Pregnancy[]): string {
    if (!pregnancies || pregnancies.length === 0) return '';
    const lastN =
      (globalThis as any)?.Constants?.lastNInputs ??
      (window as any)?.Constants?.lastNInputs ??
      15;
    return (
      UserDataFormatter.generateHeader('pregnancy') +
      pregnancies
        .slice(-lastN)
        .map(p => UserDataFormatter.formatPregnancy(p))
        .join(' ')
    );
  }

  private generateEventsMessage(events: PeriodEvent[]): string {
    if (!events || events.length === 0) return '';
    const lastN = 15
    const data =
      UserDataFormatter.generateHeader('event') +
      events
        .filter(e => !!this.eventService.hasPdAssistantDataLogged(e))
        .slice(-lastN)
        .map(e => UserDataFormatter.formatEvent(this.eventService.mapPeriodEventForFormatter(e)))
        .join(' ');
    return data + ' ';
  }
}

// TODO: Move to utils
function beginningOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toStartOfLocalDay(dateLike: string | Date): Date {
  if (dateLike instanceof Date) return this.beginningOfLocalDay(dateLike);
  if (typeof dateLike === 'string') {
    const [y, m, d] = dateLike.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
  }
  const d = new Date(dateLike as any);
  return this.beginningOfLocalDay(d);
}
